<script lang="ts">
  /**
   * The end of a solo run: the shell's `ResultsTemplate` on the deep results
   * gradient (`docs/decisions/0054`), and three claims that are never merged
   * into one number (`docs/decisions/0045`).
   *
   * 1. **Your result**, **Your personal best** and the **Global record** are
   *    three separate, labelled facts: the big figure under the headline is
   *    the result, and the facts panel says all three by name. The
   *    personal-best celebration says "personal best", never "record" or
   *    "highscore": a player once read her own best as the record everyone
   *    can see.
   * 2. **The record fact names the holder and the gap** (`state/record-gap.ts`)
   *    in its quiet line, or shows an em dash while the records have not
   *    loaded or could not be: never a stale or half-true claim.
   * 3. **Two celebrations, never one.** A new personal best has the
   *    template's personal-best ribbon. A new global record (this run beat
   *    the record and the player is signed in: the one case in which "you
   *    are now the record" is true) has the record ribbon instead, and then
   *    the personal-best fact still says "New personal best!" on its own
   *    line, so the two are both said and never confused.
   * 4. **A guest is told the score stays on this device**, with one tap to
   *    sign in, and, if the run would have been the record, that too.
   *
   * `best` is whatever `GameScreen.svelte` got back from recording the run,
   * or `null` when the mode chose not to score it; `score` is this run's own
   * figure, or `null` the same way.
   */
  import { onMount } from 'svelte';

  import { ResultsTemplate } from '@artibens/app-shell';
  import type { ResultCelebration, ResultFact } from '@artibens/app-shell';
  import { describeModeConfig, encodeModeConfig, formatClock } from '@interlock/modes';
  import type { ModeConfig, ModeOutcome, PersonalBestScore } from '@interlock/modes';

  import { account } from '../state/account.svelte.js';
  import { celebrate } from '../state/celebration.svelte.js';
  import { daily } from '../state/daily.svelte.js';
  import type { PersonalBestRecord } from '../state/personal-bests-io.js';
  import { beatsRecord, describeRecordGap } from '../state/record-gap.js';
  import { recordFor, records } from '../state/records.svelte.js';

  const {
    outcome,
    modeLabel,
    config,
    best,
    score = null,
    daily: isDaily = false,
    onRematch,
    onMenu,
    onSignIn,
  }: {
    outcome: ModeOutcome;
    modeLabel: string;
    config: ModeConfig;
    best: { best: PersonalBestRecord; isNewBest: boolean } | null;
    /** This run's own figure, as the mode scored it. */
    score?: PersonalBestScore | null;
    /**
     * Whether the run that just finished was today's daily. The rank is read
     * from the shared `daily` store rather than passed in: the result is
     * posted without waiting, so this screen renders before the answer and
     * updates when it comes.
     */
    daily?: boolean;
    onRematch: () => void;
    onMenu: () => void;
    /** Opens the account form, over this screen. */
    onSignIn: () => void;
  } = $props();

  const modeKey = $derived(encodeModeConfig(config));
  const configLabel = $derived(`${modeLabel} · ${describeModeConfig(config)}`);

  /** Not loaded covers both "has not answered yet" and "the request failed": the record fact shows an em dash either way. */
  const recordsLoaded = $derived(records.status === 'ready');
  const globalRecord = $derived(recordFor(modeKey));

  /**
   * This run beat the global record: not merely "the local store says a
   * personal best", which is also true of a first run at a configuration
   * nobody else has tried. `best.isNewBest` ties it to the run that just
   * finished, and beating the record always implies a new personal best, so
   * this never shows without the personal-best line.
   */
  const isNewGlobalRecord = $derived(
    best?.isNewBest === true &&
      recordsLoaded &&
      (globalRecord === null ||
        beatsRecord(best.best.value, globalRecord.result, best.best.betterWhen)),
  );

  const showGuestPrompt = $derived(account.status === 'guest');
  const guestWouldRecord = $derived(showGuestPrompt && isNewGlobalRecord);
  // Signed in only: a guest's run is never the record, whatever its time.
  const showRecordCelebration = $derived(isNewGlobalRecord && account.status === 'signed-in');

  const celebration = $derived<ResultCelebration>(
    showRecordCelebration ? 'record' : best?.isNewBest === true ? 'pb' : null,
  );

  /** What the record fact says under its label: the holder, and how far this run is from it. */
  const recordDetail = $derived.by((): string | undefined => {
    if (!recordsLoaded) return undefined;
    if (globalRecord === null) return 'Nobody holds this record yet. Be the first.';
    if (best === null) return `Held by ${globalRecord.username}`;
    return describeRecordGap(best.best, globalRecord, config);
  });

  const facts = $derived.by((): ResultFact[] => {
    const list: ResultFact[] = [];
    if (score !== null) {
      list.push({
        key: 'result',
        label: 'Your result',
        value: score.formatted,
        testid: 'results-result',
      });
    }
    if (best !== null) {
      // With the record ribbon up, the personal best is still said, here.
      const saidHere = best.isNewBest && showRecordCelebration;
      list.push({
        key: 'pb',
        label: 'Your personal best',
        value: best.best.formatted,
        emphasis: best.isNewBest ? 'pb' : null,
        ...(saidHere ? { detail: 'New personal best!' } : {}),
        testid: saidHere ? 'new-best-tag' : 'results-best',
      });
    }
    list.push({
      key: 'record',
      label: 'Global record',
      value: recordsLoaded && globalRecord !== null ? globalRecord.formatted : '—',
      ...(recordDetail === undefined ? {} : { detail: recordDetail }),
      emphasis: showRecordCelebration ? 'record' : null,
      testid: 'record-row',
    });
    return list;
  });

  // A burst for a new personal best, once, when the screen appears. `onMount`
  // rather than an effect: it reads a plain prop and must never re-run.
  onMount(() => {
    if (best?.isNewBest) celebrate();
  });
</script>

{#snippet lines()}
  {#if showGuestPrompt}
    <div class="guest" data-testid="guest-prompt">
      <p class="line">
        Playing as guest. This score stays on this device. Sign in to post it to the leaderboard.
      </p>
      {#if guestWouldRecord}
        <p class="line line--strong">This would be a global record. Sign in to claim it.</p>
      {/if}
      <button type="button" class="sign-in" onclick={onSignIn}>Sign in</button>
    </div>
  {/if}

  {#if isDaily && outcome.result !== 'lose'}
    <!-- A daily attempt that topped out is never posted, so there is
         nothing to report for it. -->
    <p class="line" role="status" data-testid="results-daily">
      {#if daily.submitting}
        Posting today's result…
      {:else if daily.error}
        {daily.error}
      {:else if daily.info?.played && daily.info.yourRank !== null && daily.info.yourResult !== null}
        Today's board: #{daily.info.yourRank} · {formatClock(daily.info.yourResult)}
      {:else if account.status !== 'signed-in'}
        Sign in next time to post a ranked result.
      {/if}
    </p>
  {/if}
{/snippet}

<ResultsTemplate
  eyebrow={configLabel}
  headline={outcome.title}
  focusHeading
  figure={score?.formatted}
  figureIcon={score === null ? undefined : 'trophy'}
  figureCaption={outcome.summary}
  celebrate={celebration}
  celebrateLabels={{ pb: 'New personal best!', record: 'New global record!' }}
  notice={showGuestPrompt || isDaily ? lines : undefined}
  {facts}
  primary={{ label: 'Play again', onclick: onRematch, testid: 'results-again' }}
  secondary={{ label: 'Home', onclick: onMenu, testid: 'results-menu' }}
  testids={{
    root: 'results-screen',
    headline: 'results-title',
    figure: 'results-score',
    celebrate: celebration === 'record' ? 'new-global-record' : 'new-best-tag',
  }}
/>

<style>
  /* On the deep gradient: every word in the on-deep ink, centred under the
     figure, as the template sets its own. */
  .guest {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ui-space-2);
  }

  .line {
    margin: 0;
    text-align: center;
    font: var(--ui-text-label);
    line-height: 1.45;
    color: var(--ui-on-deep);
  }

  .line--strong {
    font-weight: 800;
  }

  /* The guest's way to sign in: a quiet outlined pill on the gradient, so it
     never competes with Play again (the template's light pill). */
  .sign-in {
    min-height: 2.75rem;
    padding: 0 var(--ui-space-5);
    border: 1px solid var(--ui-on-deep);
    border-radius: var(--ui-radius-pill);
    background: transparent;
    color: var(--ui-on-deep);
    font: var(--ui-text-label);
    font-weight: 800;
  }

  .sign-in:focus-visible {
    outline-color: var(--ui-on-deep);
  }
</style>
